// Excel export matching the official RSSB Palia Kalan programme format:
// bordered grid, official header block, full date (DD/MM/YYYY) + weekday columns,
// TWO sub-rows per ghar — SK/SR on the first row, पाठी on the second row.
// Hindi Unicode names (Nirmala UI), preacher & pathi same font size.
import * as XLSX from "xlsx-js-style";
import {
  GHARS,
  PREACHERS,
  PATHIS,
  WEEKDAYS_HI,
  MONTHS_HI,
  MONTHS_EN,
  AREA_INFO,
  withHonorific,
  GHAR_TYPE_HI,
  resolvePreacherHi,
  resolvePathiHi,
  resolveSpecialHi,
  cellTime,
} from "../data/rssbData";

const FONT = "Nirmala UI";
const thin = { style: "thin", color: { rgb: "000000" } };
const BORDER = { top: thin, bottom: thin, left: thin, right: thin };
const center = { horizontal: "center", vertical: "center", wrapText: true };

const fmtDate = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

// Two separate lines: SK/SR cell text and पाठी cell text.
const skText = (cell, iso) => {
  if (!cell || !cell.type) return "";
  const tm = `${cellTime(cell, iso)}\n`;
  if (cell.type === "CD") return `${tm}सी डी सत्संग`;
  if (cell.type === "LCD") return `${tm}सवाल-जवाब सत्संग`;
  if (cell.type === "SPECIAL") return tm + resolveSpecialHi(cell, iso, false);
  return tm + (resolvePreacherHi(cell) || "XX");
};
const pathiText = (cell) => {
  if (!cell || !cell.type || cell.type === "CD" || cell.type === "LCD") return "";
  return resolvePathiHi(cell) || "XX";
};

const st = (sz, bold, extra = {}) => ({
  font: { name: FONT, sz, bold },
  alignment: center,
  ...extra,
});

export function exportExcel(variant, dates, year, month) {
  const ncols = 2 + dates.length; // क्र.सं. + जगह + date cols
  const lastC = ncols - 1;

  const aoa = [];
  const merges = [];
  const styles = {}; // "r:c" -> style

  // ---- Header block (rows 0-1) ----
  aoa[0] = []; aoa[1] = [];
  aoa[0][0] = "RS\nSB";
  merges.push({ s: { r: 0, c: 0 }, e: { r: 1, c: 1 } });
  styles["0:0"] = st(14, true, { border: BORDER });

  aoa[0][2] = AREA_INFO.orgHi;
  aoa[1][2] = `सत्संग प्रोग्राम माह ${MONTHS_HI[month]}-${year} ${AREA_INFO.areaHi} एरिया`;
  if (lastC > 2) {
    merges.push({ s: { r: 0, c: 2 }, e: { r: 0, c: lastC } });
    merges.push({ s: { r: 1, c: 2 }, e: { r: 1, c: lastC } });
  }
  styles["0:2"] = st(16, true);
  styles["1:2"] = st(12, true);

  // ---- Spacer row 2 ----
  aoa[2] = [];

  // ---- Table header (rows 3-4) ----
  const H1 = 3, H2 = 4;
  aoa[H1] = []; aoa[H2] = [];
  aoa[H1][0] = "क्र. सं.";
  aoa[H1][1] = "सत्संग जगह का नाम";
  merges.push({ s: { r: H1, c: 0 }, e: { r: H2, c: 0 } });
  merges.push({ s: { r: H1, c: 1 }, e: { r: H2, c: 1 } });
  styles[`${H1}:0`] = st(11, true, { border: BORDER });
  styles[`${H2}:0`] = st(11, true, { border: BORDER });
  styles[`${H1}:1`] = st(11, true, { border: BORDER });
  styles[`${H2}:1`] = st(11, true, { border: BORDER });
  dates.forEach((d, i) => {
    const c = 2 + i;
    aoa[H1][c] = fmtDate(d.iso);
    aoa[H2][c] = WEEKDAYS_HI[d.dow];
    styles[`${H1}:${c}`] = st(11, true, { border: BORDER });
    styles[`${H2}:${c}`] = st(10, true, { border: BORDER });
  });

  // ---- Body: TWO rows per ghar — SK/SR row + पाठी row ----
  const rowHeights = [{ hpt: 24 }, { hpt: 20 }, { hpt: 6 }, { hpt: 20 }, { hpt: 32 }];
  GHARS.forEach((g, gi) => {
    const rA = H2 + 1 + gi * 2; // SK/SR row
    const rB = rA + 1;          // पाठी row
    aoa[rA] = []; aoa[rB] = [];

    // क्र.सं. + ghar name merged across both sub-rows
    aoa[rA][0] = gi + 1;
    aoa[rA][1] = `${g.hi}–${GHAR_TYPE_HI[g.type]}`;
    merges.push({ s: { r: rA, c: 0 }, e: { r: rB, c: 0 } });
    merges.push({ s: { r: rA, c: 1 }, e: { r: rB, c: 1 } });
    const srStyle = st(10, false, { border: BORDER });
    const nameStyle = st(10, true, {
      border: BORDER,
      alignment: { horizontal: "left", vertical: "center", wrapText: true, indent: 1 },
    });
    styles[`${rA}:0`] = srStyle;
    styles[`${rB}:0`] = srStyle;
    styles[`${rA}:1`] = nameStyle;
    styles[`${rB}:1`] = nameStyle;

    dates.forEach((d, di) => {
      const c = 2 + di;
      const cell = variant.cells[g.id]?.[d.iso];
      const active = g.days.includes(d.dow);
      aoa[rA][c] = cell && cell.type ? skText(cell, d.iso) : active ? "XX" : "";
      aoa[rB][c] = cell && cell.type ? pathiText(cell) : "";
      styles[`${rA}:${c}`] = st(11, true, { border: BORDER });
      styles[`${rB}:${c}`] = st(11, false, { border: BORDER });
    });
    rowHeights.push({ hpt: 50 }, { hpt: 38 });
  });

  // ---- सेवा सारांश (duty summary) on a separate sheet ----
  const duty = {}; // personId -> ["घर dd/mm", ...] chronological
  const addDuty = (id, g, iso) => {
    if (!id) return;
    const [, m, dd] = iso.split("-");
    (duty[id] = duty[id] || []).push(`${g.hi} ${dd}/${m}`);
  };
  dates.forEach((d) =>
    GHARS.forEach((g) => {
      const cell = variant.cells[g.id]?.[d.iso];
      if (!cell || !cell.type || cell.type === "CD" || cell.type === "LCD") return;
      addDuty(cell.preacherId, g, d.iso);
      addDuty(cell.pathiId, g, d.iso);
    })
  );
  const persons = [
    ...PREACHERS.map((p) => ({ p, role: p.role === "SK" ? "एस.के." : "एस.आर." })),
    ...PATHIS.map((p) => ({ p, role: "पाठी" })),
  ].sort((a, b) => ((duty[a.p.id] || []).length ? 0 : 1) - ((duty[b.p.id] || []).length ? 0 : 1));

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!merges"] = merges;

  // Apply styles (merged regions: style every underlying cell so borders show)
  Object.keys(styles).forEach((key) => {
    const [r, c] = key.split(":").map(Number);
    const ref = XLSX.utils.encode_cell({ r, c });
    if (!ws[ref]) ws[ref] = { t: "s", v: "" };
    ws[ref].s = styles[key];
  });
  merges.forEach((m) => {
    for (let r = m.s.r; r <= m.e.r; r++)
      for (let c = m.s.c; c <= m.e.c; c++) {
        const ref = XLSX.utils.encode_cell({ r, c });
        if (!ws[ref]) ws[ref] = { t: "s", v: "" };
        if (!ws[ref].s) ws[ref].s = styles[`${m.s.r}:${m.s.c}`];
      }
  });

  // Wider columns + tall rows so every full name is clearly visible when opened/printed
  ws["!cols"] = [{ wch: 7 }, { wch: 24 }, ...dates.map(() => ({ wch: 24 }))];
  ws["!rows"] = rowHeights;

  // Print: landscape, fit all columns to one page width
  ws["!sheetPr"] = { pageSetUpPr: { fitToPage: true } };
  ws["!pageSetup"] = { orientation: "landscape", fitToWidth: 1, fitToHeight: 0 };
  ws["!margins"] = { left: 0.25, right: 0.25, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Schedule");
  XLSX.utils.book_append_sheet(wb, summarySheet(persons, duty, month, year), "सेवा सारांश");
  XLSX.writeFile(wb, `RSSB_Satsang_${MONTHS_EN[month]}_${year}.xlsx`);
}

function summarySheet(persons, duty, month, year) {
  const left = { horizontal: "left", vertical: "center", wrapText: true, indent: 1 };
  const got = persons.filter(({ p }) => (duty[p.id] || []).length).length;
  const zero = persons.length - got;
  const N = 6;
  const rows = [
    [`सेवा सारांश — ${MONTHS_HI[month]} ${year} — कुल ${persons.length} सेवादार (सेवा मिली: ${got}, शून्य सेवा: ${zero})`],
    ["क्र. सं.", "नाम", "पद", "सत्संग घर / स्थान", "सेवा संख्या", "सेवा स्थान"],
    ...persons.map(({ p, role }, i) => {
      const list = duty[p.id] || [];
      return [i + 1, withHonorific(p), role, p.placeHi || "", list.length, list.length ? list.join(", ") : "कोई सेवा नहीं (0)"];
    }),
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: N - 1 } }];
  rows.forEach((row, r) => {
    const isZero = r > 1 && row[4] === 0;
    for (let c = 0; c < N; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!ws[ref]) ws[ref] = { t: "s", v: "" };
      ws[ref].s =
        r === 0
          ? st(13, true)
          : st(r === 1 ? 11 : 10, r === 1 || isZero, {
              border: BORDER,
              ...((c === 1 || c === 3 || c === 5) && r > 1 ? { alignment: left } : {}),
              ...(isZero ? { fill: { fgColor: { rgb: "FDE2E2" } }, font: { name: FONT, sz: 10, bold: true, color: { rgb: "9B1C1C" } } } : {}),
            });
    }
  });
  ws["!cols"] = [{ wch: 7 }, { wch: 28 }, { wch: 10 }, { wch: 20 }, { wch: 11 }, { wch: 80 }];
  ws["!rows"] = [{ hpt: 24 }, { hpt: 20 }, ...persons.map(() => ({ hpt: 30 }))];
  ws["!pageSetup"] = { orientation: "landscape" };
  return ws;
}
